import React, { useEffect, useState, useCallback } from "react";
import { Lock, ChevronLeft, Plus, Trash2, Check, MessagesSquare, StickyNote, AlertTriangle, Send, X } from "lucide-react";
import { toast } from "sonner";
import { ccInput, ccCard, ccBtnPink } from "./care/ui";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const TABS = [
  { key: "threads", label: "Secure Threads", icon: MessagesSquare },
  { key: "notes", label: "Private Notes", icon: StickyNote },
  { key: "escalations", label: "Escalations", icon: AlertTriangle },
];

const ThreadCompose = ({ directory, onSend, onClose, busy, initialTo = "" }) => {
  const [to, setTo] = useState(initialTo);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  return (
    <div className="fixed inset-0 z-[100] bg-[#374151]/40 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="inbox-compose-modal" onClick={onClose}>
      <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-md w-full p-6 shadow-xl animate-modal-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-extrabold font-heading text-[#374151]">New Secure Thread</h3>
          <button onClick={onClose} data-testid="inbox-compose-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <select value={to} onChange={(e) => setTo(e.target.value)} className={`${ccInput} mb-3`} data-testid="inbox-thread-to">
          <option value="">Select a person…</option>
          {directory.map((d) => (
            <option key={d.email} value={d.email}>
              {d.name || d.email} — {{ master: `Master (${d.position || "staff"})`, daycare_admin: "Facility Owner", caregiver: "Caregiver", parent: "Parent", staff: "Staff" }[d.role] || d.role}
            </option>
          ))}
        </select>
        <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className={`${ccInput} mb-3`} data-testid="inbox-thread-subject" />
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Confidential message…" rows={4} className={`${ccInput} mb-4 resize-none`} data-testid="inbox-thread-message" />
        <button onClick={() => onSend(to, subject, message)} disabled={busy || !to || !subject.trim() || !message.trim()} className={`${ccBtnPink} w-full disabled:opacity-40`} data-testid="inbox-thread-send">
          {busy ? "Sending…" : "Start Secure Thread"}
        </button>
      </div>
    </div>
  );
};

export default function AdminInbox({ currentUser, onBack, onHome }) {
  const [tab, setTab] = useState("threads");
  const [data, setData] = useState({ notes: [], threads: [], escalations: [] });
  const [loading, setLoading] = useState(true);
  const [directory, setDirectory] = useState([]);
  const [compose, setCompose] = useState(false);
  const [initialTo, setInitialTo] = useState("");

  // Deep-link: user cards anywhere can drop a recipient here to open a pre-filled compose
  useEffect(() => {
    const pre = sessionStorage.getItem("nt_inbox_to");
    if (pre) {
      sessionStorage.removeItem("nt_inbox_to");
      setInitialTo(pre);
      setCompose(true);
    }
  }, []);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null); // {thread, messages}
  const [pinState, setPinState] = useState(null); // null=loading | "ok" | "locked" | "setup"
  const [pinInput, setPinInput] = useState("");
  const [pinBusy, setPinBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/auth/secure-pin/status`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d || !d.requires_pin) setPinState("ok");
        else if (!d.has_pin) setPinState("setup");
        else setPinState(d.unlocked ? "ok" : "locked");
      })
      .catch(() => setPinState("ok"));
  }, []);

  const verifyPin = async (e) => {
    e.preventDefault();
    setPinBusy(true);
    try {
      const res = await fetch(`${API}/api/auth/secure-pin/verify`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: pinInput }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Incorrect PIN");
      setPinState("ok");
      setPinInput("");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPinBusy(false);
    }
  };

  const requestNewPin = async () => {
    setPinBusy(true);
    try {
      const res = await fetch(`${API}/api/auth/secure-pin/request-reset`, { method: "POST", credentials: "include" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not start the PIN reset");
      if (body.reset_link) {
        await navigator.clipboard.writeText(body.reset_link).catch(() => {});
        toast.warning("Email is rate-limited — your verification link was copied to the clipboard instead.");
      } else {
        toast.success("Verification link sent to your email — open it to receive your new PIN.");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPinBusy(false);
    }
  };
  const [reply, setReply] = useState("");
  const [noteForm, setNoteForm] = useState({ title: "", body: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin-inbox`, { credentials: "include" });
      if (res.ok) setData(await res.json());
      else if (res.status === 403) setPinState((s) => (s === "ok" ? "locked" : s));
    } catch { /* keep previous */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    fetch(`${API}/api/admin-inbox/directory`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setDirectory)
      .catch(() => {});
    const t = setInterval(load, 3000); // live: new replies light up without refresh
    return () => clearInterval(t);
  }, [load]);

  const activeId = active?.thread?.id;
  useEffect(() => {
    if (!activeId) return;
    const t = setInterval(() => openThread(activeId), 3000); // live: incoming replies appear in the open conversation
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const openThread = async (id) => {
    const res = await fetch(`${API}/api/admin-inbox/threads/${id}`, { credentials: "include" });
    if (res.ok) setActive(await res.json());
  };

  const sendThread = async (to, subject, message) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin-inbox/threads`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to_email: to, subject, message }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Failed");
      toast.success("Secure thread started.");
      setCompose(false);
      load();
      openThread(body.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const deleteMessage = async (mid) => {
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API}/api/admin-inbox/threads/${active.thread.id}/messages/${mid}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Could not delete the message");
      setActive((prev) => ({ ...prev, messages: prev.messages.map((x) => (x.id === mid ? { ...x, deleted: true, text: "" } : x)) }));
      toast.success("Message deleted");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin-inbox/threads/${active.thread.id}/messages`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: reply }),
      });
      if (!res.ok) throw new Error("Send failed");
      setReply("");
      openThread(active.thread.id);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteForm.title.trim()) return;
    try {
      const res = await fetch(`${API}/api/admin-inbox/notes`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(noteForm),
      });
      if (!res.ok) throw new Error("Could not save note");
      setNoteForm({ title: "", body: "" });
      toast.success("Note saved.");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleNote = async (id, done) => {
    await fetch(`${API}/api/admin-inbox/notes/${id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ done }),
    });
    load();
  };

  const deleteNote = async (id) => {
    await fetch(`${API}/api/admin-inbox/notes/${id}`, { method: "DELETE", credentials: "include" });
    toast.success("Note deleted.");
    load();
  };

  if (pinState === "locked") {
    return (
      <div className="min-h-screen bg-[#FFF8E7] flex items-center justify-center p-6" data-testid="inbox-pin-lock">
        <form onSubmit={verifyPin} className="bg-white border border-[#FCE6E9] rounded-3xl p-8 max-w-sm w-full shadow-xl text-center">
          <span className="w-12 h-12 rounded-full bg-[#FCE6E9] flex items-center justify-center mx-auto mb-4">
            <Lock className="w-5 h-5 text-[#B04A6B]" />
          </span>
          <h2 className="text-lg font-black font-heading text-[#374151] mb-1">Secure inbox locked</h2>
          <p className="text-xs text-[#9CA3AF] font-medium mb-5">Enter the 4-digit PIN from your invite email. Lost or compromised? Request a new one — a verification link goes to your email.</p>
          <input
            type="password"
            inputMode="numeric"
            maxLength={4}
            required
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="••••"
            className="w-full text-center text-2xl font-black tracking-[0.5em] px-4 py-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl focus:outline-none focus:border-[#E5A0BC] mb-3"
            data-testid="inbox-pin-input"
          />
          <button type="submit" disabled={pinBusy || pinInput.length !== 4} className="w-full px-4 py-2.5 bg-[#374151] hover:bg-[#1F2937] text-white rounded-full text-xs font-extrabold transition-colors disabled:opacity-50 mb-2" data-testid="inbox-pin-submit">
            {pinBusy ? "Verifying…" : "Unlock secure inbox"}
          </button>
          <button type="button" onClick={requestNewPin} disabled={pinBusy} className="w-full px-4 py-2 text-[11px] font-extrabold text-[#5CB3DC] hover:underline disabled:opacity-50" data-testid="inbox-pin-reset">
            Forgot / compromised? Email me a new PIN
          </button>
          <button type="button" onClick={onBack} className="w-full px-4 py-1 text-[10px] font-bold text-[#9CA3AF] hover:text-[#374151]" data-testid="inbox-pin-back">
            Back
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8E7] text-[#374151] dark:bg-[#1F2937] dark:text-slate-200" data-testid="admin-inbox">
      {pinState === "setup" && (
        <div className="mx-4 sm:mx-8 mt-4 p-3.5 bg-[#FFF2D1] border border-[#F5E3B3] rounded-2xl flex items-center justify-between gap-3 flex-wrap" data-testid="inbox-pin-setup-banner">
          <p className="text-[11px] font-bold text-[#8a6d1a]">Your secure inbox has no PIN yet — set one to lock down staff messaging.</p>
          <button onClick={requestNewPin} disabled={pinBusy} className="px-3.5 py-1.5 bg-[#8a6d1a] text-white rounded-full text-[10px] font-extrabold disabled:opacity-50" data-testid="inbox-pin-setup-btn">
            Email me my PIN
          </button>
        </div>
      )}
      <header className="border-b border-[#FCE6E9] bg-white/80 backdrop-blur-xl px-4 sm:px-6 py-3.5 flex items-center justify-between sticky top-0 z-40 dark:bg-[#232D3D]/90 dark:border-[#374457]">
        <div className="flex items-center gap-3">
          <button onClick={onBack} data-testid="inbox-back-btn" className="flex items-center gap-1 px-3.5 py-2 bg-white border border-[#E6F2FA] rounded-full text-xs font-bold text-[#374151] hover:bg-[#E6F2FA] transition-all dark:bg-[#273244] dark:border-[#374457] dark:text-slate-200">
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
          <button onClick={onHome} className="flex items-center gap-2" data-testid="inbox-home-logo" title="Back to home page">
            <img src="/assets/logo.png" alt="NestTrack" className="w-8 h-8 rounded-full border-2 border-[#FCE6E9] object-cover" />
            <span className="font-black text-lg tracking-tight font-heading hidden sm:inline">
              Nest<span className="text-[#E5A0BC]">Track</span>
            </span>
          </button>
        </div>
        <span className="flex items-center gap-1.5 px-3 py-1.5 bg-[#374151] text-white text-[10px] font-extrabold uppercase tracking-wider rounded-full" data-testid="inbox-secure-badge">
          <Lock className="w-3 h-3" /> Admin Only
        </span>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-black font-heading" data-testid="inbox-title">Secure Inbox</h1>
          <p className="text-sm text-[#9CA3AF] font-medium mt-1">Confidential admin threads, private notes & escalated inquiries — invisible to staff and parents.</p>
        </div>

        <div className="flex gap-2 flex-wrap" data-testid="inbox-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`inbox-tab-${key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition-all ${
                tab === key ? "bg-[#374151] text-white shadow-sm" : "bg-white border border-[#FCE6E9] text-[#6B7280] hover:border-[#F8C8DC] dark:bg-[#273244] dark:border-[#374457]"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
              {key === "escalations" && data.escalations.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-rose-500 text-white text-[9px] font-black rounded-full" data-testid="inbox-escalation-count">{data.escalations.length}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-xs text-[#9CA3AF] font-semibold py-8 text-center">Loading secure workspace…</p>
        ) : tab === "threads" ? (
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
            <div className={`${ccCard} p-4 space-y-2`}>
              <button onClick={() => setCompose(true)} className={`${ccBtnPink} w-full flex items-center justify-center gap-2 mb-2`} data-testid="inbox-new-thread-btn">
                <Plus className="w-4 h-4" /> New Secure Thread
              </button>
              {data.threads.length === 0 ? (
                <p className="text-xs text-[#9CA3AF] font-semibold py-6 text-center" data-testid="inbox-threads-empty">No secure threads yet — start one with another admin.</p>
              ) : (
                data.threads.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => openThread(t.id)}
                    data-testid={`inbox-thread-row-${t.id}`}
                    className={`w-full text-left p-3 rounded-2xl border transition-all ${active?.thread?.id === t.id ? "bg-[#FCE6E9] border-[#F8C8DC]" : "bg-[#FFF9F2] border-[#FCE6E9] hover:border-[#F8C8DC]"} dark:bg-[#273244] dark:border-[#374457]`}
                  >
                    <p className="text-xs font-black truncate">
                      {t.subject}
                      {t.unread && <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-rose-500 align-middle" data-testid={`inbox-unread-${t.id}`} />}
                    </p>
                    <p className="text-[10px] text-[#9CA3AF] font-medium truncate">with {t.other_name} · {t.lastMessage}</p>
                    <p className="text-[9px] text-[#C4B5A0] font-bold mt-0.5">{fmtTs(t.lastTs)}</p>
                  </button>
                ))
              )}
            </div>
            <div className={`${ccCard} p-4 flex flex-col min-h-[320px]`} data-testid="inbox-conversation">
              {!active ? (
                <p className="text-xs text-[#9CA3AF] font-semibold m-auto">Select a thread to read the conversation.</p>
              ) : (
                <>
                  <p className="text-sm font-extrabold font-heading mb-3 pb-3 border-b border-[#FCE6E9] dark:border-[#374457]">{active.thread.subject}</p>
                  <div className="flex-1 space-y-2.5 overflow-y-auto max-h-[380px] pr-1" data-testid="inbox-messages">
                    {active.messages.map((m) => {
                      const mine = m.sender_email === currentUser?.email;
                      return (
                        <div key={m.id} className={`group relative max-w-[80%] p-3 rounded-2xl text-xs font-medium ${mine ? "ml-auto bg-[#374151] text-white rounded-br-md" : "bg-[#FFF9F2] border border-[#FCE6E9] rounded-bl-md dark:bg-[#273244] dark:border-[#374457]"}`} data-testid={`inbox-msg-${m.id}`}>
                          {(mine || currentUser?.role === "master") && !m.deleted && (
                            <button onClick={() => deleteMessage(m.id)} title="Delete message" data-testid={`inbox-delete-${m.id}`} className={`absolute -top-2 ${mine ? "-left-2" : "-right-2"} opacity-0 group-hover:opacity-100 transition-opacity w-5 h-5 rounded-full bg-white border border-[#FCE6E9] text-[#B04A6B] flex items-center justify-center hover:bg-[#FCE6E9] shadow-sm`}>
                              <Trash2 className="w-2.5 h-2.5" />
                            </button>
                          )}
                          <p className={`text-[9px] font-extrabold uppercase tracking-wide mb-1 ${mine ? "text-white/60" : "text-[#E5A0BC]"}`}>{m.sender_name || m.sender_email}</p>
                          {m.deleted ? <p className="italic opacity-60" data-testid={`inbox-deleted-${m.id}`}>Message deleted</p> : <p className="whitespace-pre-wrap">{m.text}</p>}
                          <p className={`text-[9px] mt-1 ${mine ? "text-white/50" : "text-[#C4B5A0]"}`}>{fmtTs(m.ts)}</p>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 mt-3 pt-3 border-t border-[#FCE6E9] dark:border-[#374457]">
                    <input type="text" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Write a secure reply…" className={ccInput} data-testid="inbox-reply-input" />
                    <button onClick={sendReply} disabled={busy || !reply.trim()} className={`${ccBtnPink} shrink-0 flex items-center gap-1.5 disabled:opacity-40`} data-testid="inbox-reply-send">
                      <Send className="w-3.5 h-3.5" /> Send
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : tab === "notes" ? (
          <div className="space-y-4">
            <form onSubmit={addNote} className={`${ccCard} p-4 grid grid-cols-1 sm:grid-cols-[1fr_1.4fr_auto] gap-3`} data-testid="inbox-note-form">
              <input type="text" required value={noteForm.title} onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })} placeholder="Note title / reminder" className={ccInput} data-testid="inbox-note-title" />
              <input type="text" value={noteForm.body} onChange={(e) => setNoteForm({ ...noteForm, body: e.target.value })} placeholder="Details (optional)" className={ccInput} data-testid="inbox-note-body" />
              <button type="submit" className={`${ccBtnPink} shrink-0 flex items-center gap-1.5`} data-testid="inbox-note-add">
                <Plus className="w-4 h-4" /> Save Note
              </button>
            </form>
            {data.notes.length === 0 ? (
              <p className="text-xs text-[#9CA3AF] font-semibold py-8 text-center" data-testid="inbox-notes-empty">No private notes yet — only you can see what you save here.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.notes.map((n) => (
                  <div key={n.id} className={`${ccCard} p-4 ${n.done ? "opacity-50" : ""}`} data-testid={`inbox-note-${n.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-extrabold font-heading ${n.done ? "line-through" : ""}`}>{n.title}</p>
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => toggleNote(n.id, !n.done)} title={n.done ? "Mark not done" : "Mark done"} data-testid={`inbox-note-done-${n.id}`} className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${n.done ? "bg-[#7CC98F] text-white" : "bg-[#E2F4E1] text-[#2F6031] hover:bg-[#C4E5C2]"}`}>
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => deleteNote(n.id)} title="Delete note" data-testid={`inbox-note-del-${n.id}`} className="w-7 h-7 rounded-full bg-rose-50 text-rose-400 hover:bg-[#FCE6E9] flex items-center justify-center transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {n.body && <p className="text-xs text-[#6B7280] font-medium mt-1.5 whitespace-pre-wrap">{n.body}</p>}
                    <p className="text-[9px] text-[#C4B5A0] font-bold mt-2">{fmtTs(n.createdAt)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2.5">
            {data.escalations.length === 0 ? (
              <p className="text-xs text-[#9CA3AF] font-semibold py-8 text-center" data-testid="inbox-escalations-empty">No escalated inquiries right now.</p>
            ) : (
              data.escalations.map((c) => (
                <div key={c.id} className={`${ccCard} p-4 flex items-start justify-between gap-3`} data-testid={`inbox-escalation-${c.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-extrabold font-heading truncate">{c.subject}</p>
                    <p className="text-xs text-[#6B7280] font-medium mt-0.5 line-clamp-2">{c.message}</p>
                    <p className="text-[10px] text-[#9CA3AF] font-bold mt-1.5">
                      from {c.fromName || c.fromEmail} {c.region ? `· ${String(c.region).toUpperCase()}` : ""} · {fmtTs(c.createdAt)}
                    </p>
                  </div>
                  <span className={`px-2.5 py-1 text-[9px] font-extrabold uppercase rounded-full shrink-0 ${c.elevated ? "bg-rose-50 text-rose-500 border border-rose-200" : "bg-[#FFF2D1] text-[#D9A93E] border border-[#F5E3B3]"}`}>
                    {c.elevated ? "Elevated" : c.status}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </main>

      {compose && <ThreadCompose directory={directory} onSend={sendThread} onClose={() => setCompose(false)} busy={busy} initialTo={initialTo} />}
    </div>
  );
}
